import { Flex, Spinner } from '@chakra-ui/react';
import { useCallback } from 'react';
import { useSnackbar } from 'ui-components';
import { Button } from 'ui-components/dist/Button';
import { SOCIAL_PLATFORM_PIPELINE_STATUS } from '~/clientApi/offPlatform.model';
import { useAddPlatform } from '~/hooks/api/useAddPlatform';
import {
  filterPlatformsByStatus,
  parseHandle,
} from '~/screens/ManagePlatforms';
import {
  DataNotAvailable,
  Error,
} from '~/screens/ManagePlatforms/AddNewPlatformsModal';
import { useAddNewPlatformsStore } from '~/screens/ManagePlatforms/AddNewPlatformsModal/useAddNewPlatformsStore';
import { useEditPlatformsStore } from '~/screens/ManagePlatforms/EditPlatformsModal_DEPRECATED';
import {
  PlatformInfo,
  SocialAccountType,
} from '~/screens/ManagePlatforms/common';

export const ConfirmPlatform: React.FC = () => {
  const snackbar = useSnackbar();

  const { values, platform, setPlatform, setIsModalOpen } =
    useAddNewPlatformsStore();
  const { creator } = useEditPlatformsStore();

  const addPlatform = useAddPlatform();

  const handleBack = useCallback(() => setPlatform(undefined), [setPlatform]);

  const handleAddNewPlatform = useCallback(async () => {
    const { instagram, tiktok, snapchat, youtube } = values;
    const payload = {
      igHandle: parseHandle(instagram),
      ttHandle: parseHandle(tiktok),
      snapHandle: parseHandle(snapchat),
      ytHandle: parseHandle(youtube),
      id: creator?.id as string,
    };
    addPlatform.mutate(
      {
        id: payload.id,
        creator: payload,
        type: 'json',
      },
      {
        onError: () => {
          snackbar.error({
            description: 'Something went wrong, please try again!',
          });
        },
        onSuccess: () => {
          snackbar.success({
            description: 'Platform successfully added',
          });
        },
        onSettled: () => {
          setIsModalOpen(false);
        },
      },
    );
  }, [values, creator?.id, addPlatform, snackbar, setIsModalOpen]);

  const validPlatforms = filterPlatformsByStatus(
    platform,
    SOCIAL_PLATFORM_PIPELINE_STATUS.finished,
  );
  const notPublicPlatforms = filterPlatformsByStatus(
    platform,
    SOCIAL_PLATFORM_PIPELINE_STATUS.invalid,
  );

  return (
    <>
      <Flex direction="column" gap={4}>
        {addPlatform.isError ? (
          <Error />
        ) : (
          <>
            {validPlatforms.map(([key, value]: [string, SocialAccountType]) => (
              <PlatformInfo
                key={key}
                platform={key}
                avatar={value.profilePictureUrl}
                name={value.name}
                handle={value.handle}
                followers={value.followersCount}
              />
            ))}
            {notPublicPlatforms.length > 0 && (
              <DataNotAvailable notPublicPlatforms={notPublicPlatforms} />
            )}
          </>
        )}
      </Flex>
      <Flex justifyContent="flex-end" marginTop={4} gap={2}>
        <Button variant="tertiary" size="md" onClick={handleBack}>
          Back
        </Button>
        {!addPlatform.isError ? (
          <Button
            size="md"
            onClick={handleAddNewPlatform}
            isDisabled={
              addPlatform.isLoading ||
              (!values.instagram &&
                !values.tiktok &&
                !values.snapchat &&
                !values.youtube)
            }
            leftIcon={
              addPlatform.isLoading ? <Spinner boxSize={4} /> : undefined
            }
            trackId="foam-talent-added-platform"
          >
            Add
          </Button>
        ) : null}
      </Flex>
    </>
  );
};
